// @ts-check

/**
 * Personaliser — unified engine for basic (engraving) and advanced
 * (JSON-config) product personalisation.
 *
 * The block (blocks/personaliser.liquid) embeds a JSON payload:
 *   { config, context, variants, addonStock, t }
 * Basic products get a config generated in Liquid from metafields, so this
 * engine only ever deals with one config shape.
 *
 * CART CONTRACT (read by the Cart Transform function — do not rename):
 *   _personalisation_data   JSON { selections, price_additions, config_handle }
 *   _engraving_sides_count  number of engraved sides (basic pricing)
 *   _zero_price_addon       'true' on add-on lines that should cost 0
 * Ops-facing (order display, keep stable): _bundle_id, _addon_for, _from_sku,
 * _engraving_note, _personalised_{SIDE}_TEXT/_FONT + human-readable copies.
 *
 * _bundle_id links the main line to its add-on lines; the cart
 * (component-cart-items.js) removes/re-quantifies them together.
 */

import { DialogComponent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { UpgradeEvents, readUpgrade } from '@theme/upgrade-product';

import { decodeEntitiesDeep } from '@theme/utilities';
function cartAddUrl() {
  return typeof Theme !== 'undefined' ? Theme.routes.cart_add_url : '/cart/add.js';
}

/**
 * How long to wait for product-form to announce the main add before checking
 * the cart for stranded add-on lines. On a warm page the announcement lands in
 * a microtask, so silence this long means the add never started.
 */
const ADDON_GUARD_TIMEOUT = 3000;

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Fonts with special behaviour (character limits, composed previews).
 * `files` lists the woff2 basenames to load for the font.
 */
const FONT_BEHAVIOURS = {
  'round-monogram': {
    maxLength: 3,
    noWhitespace: true,
    dropdownFamily: 'serif',
    files: ['round-monogram-l', 'round-monogram-c', 'round-monogram-r'],
  },
  interlocking: {
    maxLength: 3,
    noWhitespace: true,
    dropdownFamily: 'serif',
    files: ['interlocking'],
  },
};

const SWATCH_COLORS = [
  [/rose[_\s-]?gold|rose/, '#e3a177'],
  [/gold/, '#d4af37'],
  [/silver|white/, '#c0c0c0'],
];

const METAL_STYLE_MAP = {
  sterling_silver: 'silver',
  silver: 'silver',
  '9ct_white_gold': 'silver',
  white_gold: 'silver',
  gold: 'gold',
  '9ct_gold': 'gold',
  '9ct_yellow_gold': 'gold',
  gold_plated: 'gold',
  gold_plated_sterling_silver: 'gold',
  rose_gold: 'rose_gold',
  rose_gold_plated: 'rose_gold',
  rose_gold_plated_sterling_silver: 'rose_gold',
};

/** @param {string} text */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** @param {string} str */
function titleCase(str) {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Strip leading verb from a field label: "Choose Metal" → "Metal" */
function cleanLabel(label) {
  return titleCase(label.replace(/^(Choose|Select|Enter|Add) (a )?/i, ''));
}

/** Preview font size (px) by text length */
function previewFontSize(text) {
  const len = text.length;
  if (len <= 3) return '60px';
  if (len <= 8) return '40px';
  if (len <= 15) return '32px';
  if (len <= 25) return '28px';
  if (len <= 40) return '19px';
  return '15px';
}

function caretSvg() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', 'icon icon-caret');
  svg.setAttribute('viewBox', '0 0 10 6');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('fill-rule', 'evenodd');
  path.setAttribute('clip-rule', 'evenodd');
  path.setAttribute(
    'd',
    'M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z'
  );
  path.setAttribute('fill', 'currentColor');
  svg.appendChild(path);
  return svg;
}

/**
 * @typedef {object} PersonaliserRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLScriptElement} payload
 * @property {HTMLElement} fieldsContainer
 * @property {HTMLElement} previewPanel
 * @property {HTMLElement} progress
 * @property {HTMLElement} propertiesBucket
 * @property {HTMLElement} turnstileContainer
 * @property {HTMLElement} basePrice
 * @property {HTMLElement} additionsRow
 * @property {HTMLElement} additionsPrice
 * @property {HTMLElement} totalPrice
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} readyMessage
 * @property {HTMLElement} errorMessage
 *
 * @extends DialogComponent
 */
class PersonaliserComponent extends DialogComponent {
  // The block wrapper renders on every product; only products with a
  // personalisation config get the dialog + payload, so no hard requirements.
  requiredRefs = [];

  /** @type {Record<string, any>} */
  selections = {};
  priceAdditions = 0; // major units, matches _personalisation_data contract
  #rendered = false;
  /** @type {Promise<void> | null} */
  #fontsLoaded = null;
  currentStepIndex = 0;
  visitedSteps = new Set([0]);
  summaryExpanded = true;
  #turnstileWidgetId = null;
  #turnstileToken = null;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.payload || !this.refs.dialog) return;

    const data = JSON.parse(this.refs.payload.textContent || '{}');

    /*
     * Decode the WHOLE payload, not just `t`. Field labels come through
     * blocks/personaliser.liquid as `{{ side_label_display | json }}`, where
     * side_label_display is an `engraving_positions.*` locale lookup - so `t`
     * escapes it exactly like the strings in `t` itself, and French labels
     * arrived as "Gravure exterieur de l&#39;anneau". Advanced mode's config
     * comes from a metaobject JSON field, which Translate & Adapt also stores
     * escaped. Everything here is rendered with textContent, so decoding the
     * payload as a whole is both correct and the only version that does not
     * leave a subtree behind next time a string is added.
     */
    decodeEntitiesDeep(data);
    this.config = data.config || { fields: [] };
    this.context = data.context || {};
    this.variants = data.variants || {};
    this.addonStock = data.addonStock || {};
    this.t = data.t || {};

    this.layoutMode = this.config.layout_mode || 'single_form';
    this.steps = this.config.steps || [];
    if (this.layoutMode === 'steps' && !this.#stepsConfigValid()) {
      this.layoutMode = 'single_form';
    }

    // Keep base price in sync with variant changes (variant picker morphs
    // only itself; we listen for the standard product select event).
    const target = this.closest('.shopify-section') || document;
    target.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#abortController.signal,
    });

    // Openers outside this component (e.g. the sticky add-to-cart bar's
    // Personalise button) use [data-personaliser-open] within the section.
    document.addEventListener(
      'click',
      (event) => {
        const trigger =
          event.target instanceof Element ? event.target.closest('[data-personaliser-open]') : null;
        if (!trigger) return;
        if (trigger.closest('.shopify-section') !== this.closest('.shopify-section')) return;
        this.showDialog();
      },
      { signal: this.#abortController.signal }
    );

    // Upgrades own their own tick boxes — including the ones projected into
    // this modal's footer — so the engine only tracks them for the total.
    document.addEventListener(UpgradeEvents.change, this.#onUpgradeChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #onProductSelect = (event) => {
    // The event promise resolves after the variant picker morph completes,
    // at which point input[name="id"] holds the new variant id.
    event.promise?.then(() => this.#updateBasePrice()).catch(() => {});
  };

  // ==========================================================================
  // Upgrades
  //
  // Each upgrade-product block owns its tick boxes and its cart line, and
  // projects a copy of its row into this modal's footer slot. The engine never
  // touches that state — it only mirrors the prices in the running total.
  // ==========================================================================

  /** @type {Map<string, {label: string, priceMinor: number, checked: boolean}>} */
  #upgrades = new Map();

  get #upgradesTotalMinor() {
    let total = 0;
    for (const upgrade of this.#upgrades.values()) {
      if (upgrade.checked) total += upgrade.priceMinor;
    }
    return total;
  }

  /** Reads the current state of every upgrade in this section from the DOM. */
  #readUpgrades() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#upgrades.clear();

    for (const component of section.querySelectorAll('upgrade-product-component')) {
      const upgrade = readUpgrade(component);
      if (!upgrade) continue;

      this.#upgrades.set(upgrade.variantId, {
        label: upgrade.label,
        priceMinor: upgrade.priceMinor,
        checked: upgrade.checked,
      });
    }
  }

  /** @param {CustomEvent} event */
  #onUpgradeChange = (event) => {
    const section = this.closest('.shopify-section');
    if (!section || !(event.target instanceof Node) || !section.contains(event.target)) return;

    const variantId = String(event.detail?.upgradeVariantId ?? '');
    if (!variantId) return;

    this.#upgrades.set(variantId, {
      label: String(event.detail?.label || ''),
      priceMinor: Number(event.detail?.priceMinor) || 0,
      checked: Boolean(event.detail?.checked),
    });

    this.#updatePricing();
  };

  #renderUpgradeRows() {
    const container = this.refs.upgradeRows;
    if (!container) return;

    const rows = [];

    for (const upgrade of this.#upgrades.values()) {
      if (!upgrade.checked || upgrade.priceMinor <= 0) continue;

      const row = document.createElement('div');
      row.className = 'personaliser__price-row';

      const label = document.createElement('span');
      label.textContent = upgrade.label;

      const price = document.createElement('span');
      price.textContent = this.formatMoney(upgrade.priceMinor);

      row.append(label, price);
      rows.push(row);
    }

    container.replaceChildren(...rows);
  }

  #stepsConfigValid() {
    return (
      this.steps.length > 0 &&
      Array.isArray(this.config.fields) &&
      this.steps.every((step) => step.id && step.label && step.sections?.length)
    );
  }

  // ==========================================================================
  // Dialog lifecycle
  // ==========================================================================

  showDialog() {
    super.showDialog();
    this.#onOpen();
  }

  async #onOpen() {
    this.#setError('');
    this.#resetAddButton();
    this.#readUpgrades();
    this.#updateBasePrice();

    if (!this.#rendered) {
      await this.#preloadFonts();
      this.#applyDefaultSelections();

      if (this.layoutMode === 'steps') {
        this.#renderSteppedLayout();
      } else {
        this.#renderSingleFormLayout();
      }

      this.#renderPreviewPanel();
      this.#rendered = true;
    }

    if (this.#hasImageFields()) this.#loadTurnstileScript();

    this.#refresh();
  }

  /** Re-evaluate visibility, pricing, previews, properties and step state. */
  #refresh() {
    this.#updateFieldVisibility();
    this.#updateVariantSelections();
    this.#updatePricing();
    this.#updatePreviews();
    this.#updateSelectionSummary();
    this.#updatePreviewPanelState();
    if (this.layoutMode === 'steps') {
      this.#updateStepStates();
      this.#updateStepNavigation();
    }
  }

  #resetAddButton() {
    const { addButton } = this.refs;
    if (!addButton) return;
    addButton.disabled = false;
    addButton.textContent = this.t.addToCart || 'Add to basket';
  }

  #setError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.hidden = !message;
  }

  // ==========================================================================
  // Money / variants
  // ==========================================================================

  /** @param {number} minorUnits */
  formatMoney(minorUnits) {
    // Locale-aware symbol position (de: "11,95 €"), matching <localized-money>
    // used for the server-rendered prices. Falls back to the shop money format
    // if Intl rejects the currency code.
    const currency = this.context.currency || 'GBP';
    try {
      const locale = document.documentElement.lang || 'en';
      const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
      const digits = fmt.resolvedOptions().maximumFractionDigits;
      return fmt.format(Math.round(minorUnits) / Math.pow(10, digits));
    } catch (e) {
      return formatMoney(
        Math.round(minorUnits),
        this.context.moneyFormat || '£{{amount}}',
        currency
      );
    }
  }

  get #mainForm() {
    return /** @type {HTMLFormElement | null} */ (
      document.getElementById(this.context.formId)
    );
  }

  get #currentVariant() {
    const id = this.#mainForm?.querySelector('input[name="id"]')?.value;
    return (id && this.variants[id]) || Object.values(this.variants)[0] || {};
  }

  /** Base price in minor units */
  get basePriceMinor() {
    return this.#currentVariant.price || 0;
  }

  #updateBasePrice() {
    if (this.refs.basePrice) {
      this.refs.basePrice.textContent = this.formatMoney(this.basePriceMinor);
    }
    this.#updatePricing();
  }

  // ==========================================================================
  // Fonts
  // ==========================================================================

  get #selectableFonts() {
    return (this.config.fonts || []).map((f) =>
      typeof f === 'string' ? f : f.name
    );
  }

  /**
   * Map of font-family name → source URL for every font the config needs.
   * All fonts follow one convention: a Shopify Files upload named
   * personaliser-{slug}.woff2, where slug = font name lowercased with
   * spaces → dashes. Faces are registered under the exact family name
   * configs/CSS use.
   */
  #fontSources() {
    const baseUrl = this.context.fontBaseUrl || '';
    const sources = new Map();

    const add = (name) => {
      const behaviour = FONT_BEHAVIOURS[name];
      if (behaviour) {
        behaviour.files.forEach((f) => sources.set(f, `${baseUrl}personaliser-${f}.woff2`));
        return;
      }
      const slug = name.toLowerCase().replace(/\s+/g, '-');
      sources.set(name, `${baseUrl}personaliser-${slug}.woff2`);
    };

    for (const font of this.config.font_files || this.#selectableFonts) add(font);

    for (const field of this.config.fields || []) {
      if (field.default_font) add(field.default_font);
    }

    return sources;
  }

  #preloadFonts() {
    if (this.#fontsLoaded) return this.#fontsLoaded;

    this.#fontsLoaded = Promise.all(
      [...this.#fontSources()].map(([family, url]) => {
        const fontFace = new FontFace(family, `url(${url})`);
        return fontFace
          .load()
          .then(() => document.fonts.add(fontFace))
          .catch((err) => console.warn(`[personaliser] font failed: ${family}`, err));
      })
    ).then(() => undefined);

    return this.#fontsLoaded;
  }

  // ==========================================================================
  // Defaults & visibility
  // ==========================================================================

  #applyDefaultSelections() {
    for (const field of this.config.fields || []) {
      if (field.default_value && field.options) {
        const option = field.options.find((o) => o.value === field.default_value);
        if (option) {
          this.selections[field.key] = {
            value: option.value,
            label: option.label,
            label_internal: option.label_internal || option.label,
            price: option.price || 0,
            variant_id: option.variant_id || null,
          };
        }
      }
      // Default font goes into the cart payload even if the dropdown still
      // shows its placeholder (matches previous behaviour).
      if (field.type === 'text' && field.font_selector && field.default_font) {
        this.selections[`${field.key}_font`] = field.default_font;
      }
    }
  }

  isFieldVisible(field) {
    if (!field.show_if) return true;

    const condition = field.show_if;
    const parentField = (this.config.fields || []).find((f) => f.key === condition.field);
    if (parentField && !this.isFieldVisible(parentField)) return false;

    const selection = this.selections[condition.field];

    // Unchecked checkbox behaves as value "no"
    if (!selection) {
      if (parentField?.type === 'checkbox') return condition.value === 'no';
      return false;
    }

    const selectedValue = typeof selection === 'object' ? selection.value : selection;
    if (condition.values) return condition.values.includes(selectedValue);
    return selectedValue === condition.value;
  }

  #updateFieldVisibility() {
    let newlyVisibleImageField = null;

    for (const field of this.config.fields || []) {
      const wrapper = this.refs.fieldsContainer?.querySelector(
        `[data-field-key="${field.key}"]`
      );
      if (!wrapper) continue;

      const wasVisible = wrapper.dataset.visible === 'true';
      const visible = this.isFieldVisible(field);
      wrapper.hidden = !visible;
      wrapper.dataset.visible = String(visible);

      if (!wasVisible && visible && field.type === 'image') {
        newlyVisibleImageField = wrapper;
      }
    }

    if (newlyVisibleImageField) {
      setTimeout(() => {
        newlyVisibleImageField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }

  // ==========================================================================
  // Layout rendering
  // ==========================================================================

  #renderSingleFormLayout() {
    const container = this.refs.fieldsContainer;
    container.innerHTML = '';

    // Group fields by section, preserving order
    const sections = new Map();
    for (const field of this.config.fields || []) {
      const key = field.section || 'default';
      if (!sections.has(key)) {
        sections.set(key, { label: field.section_label || field.label, fields: [] });
      }
      sections.get(key).fields.push(field);
    }

    for (const section of sections.values()) {
      const sectionEl = document.createElement('div');
      sectionEl.className = 'personaliser__form-section personaliser__step';

      if (sections.size > 1) {
        const header = document.createElement('h3');
        header.className = 'personaliser__form-section-title h5';
        header.textContent = section.label;
        sectionEl.appendChild(header);
      }

      for (const field of section.fields) {
        const el = this.#createField(field);
        if (el) sectionEl.appendChild(el);
      }

      container.appendChild(sectionEl);
    }
  }

  #renderSteppedLayout() {
    const container = this.refs.fieldsContainer;
    container.innerHTML = '';

    const { progress } = this.refs;
    if (progress) {
      progress.hidden = false;
      this.#renderProgressBar();
    }

    this.steps.forEach((step, index) => {
      const stepEl = document.createElement('div');
      stepEl.className = 'personaliser__step';
      stepEl.dataset.stepIndex = String(index);
      if (index !== 0) stepEl.classList.add('is-hidden');

      if (index > 0) {
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'personaliser__step-back';
        back.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> ${escapeHtml(this.t.previous || 'Back')}`;
        back.addEventListener('click', () => this.goToStep(index - 1));
        stepEl.appendChild(back);
      }

      for (const field of this.#fieldsForStep(step)) {
        const el = this.#createField(field);
        if (el) stepEl.appendChild(el);
      }

      if (index < this.steps.length - 1) {
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'button personaliser__step-next';
        next.textContent = this.t.next || 'Continue';
        next.addEventListener('click', () => this.#handleNextStep());
        stepEl.appendChild(next);
      }

      container.appendChild(stepEl);
    });
  }

  #renderProgressBar() {
    const { progress } = this.refs;
    progress.innerHTML = '';

    this.steps.forEach((step, index) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'personaliser__progress-item';
      item.dataset.stepIndex = String(index);
      if (index === 0) item.classList.add('is-current');
      item.innerHTML = `
        <span class="personaliser__progress-indicator">
          <span class="personaliser__progress-number">${index + 1}</span>
          <svg class="personaliser__progress-tick" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
        <span class="personaliser__progress-label">${escapeHtml(step.label)}</span>
      `;
      item.addEventListener('click', () => this.#handleProgressClick(index));
      progress.appendChild(item);

      if (index < this.steps.length - 1) {
        const connector = document.createElement('div');
        connector.className = 'personaliser__progress-connector';
        progress.appendChild(connector);
      }
    });
  }

  #fieldsForStep(step) {
    return (this.config.fields || []).filter((f) => step.sections.includes(f.section));
  }

  #stepIndexForField(field) {
    return this.steps.findIndex((step) => step.sections.includes(field.section));
  }

  // ==========================================================================
  // Step navigation
  // ==========================================================================

  #fieldComplete(field) {
    if (!this.isFieldVisible(field) || !field.required) return true;
    const sel = this.selections[field.key];
    switch (field.type) {
      case 'select':
      case 'variant_select':
        return Boolean(sel?.value);
      case 'text':
        return typeof sel === 'string' && sel.trim().length > 0;
      case 'image':
        return Boolean(sel?.url);
      case 'checkbox':
        return sel?.checked === true;
      default:
        return true;
    }
  }

  isStepComplete(stepIndex) {
    const step = this.steps[stepIndex];
    if (!step) return false;
    return this.#fieldsForStep(step).every((field) => this.#fieldComplete(field));
  }

  #allRequiredFieldsComplete() {
    return (this.config.fields || []).every((field) => this.#fieldComplete(field));
  }

  #handleProgressClick(targetIndex) {
    if (targetIndex <= this.currentStepIndex) {
      this.goToStep(targetIndex);
      return;
    }
    for (let i = 0; i < targetIndex; i++) {
      if (!this.isStepComplete(i)) {
        this.goToStep(i);
        this.#highlightMissingFields(i);
        return;
      }
    }
    this.goToStep(targetIndex);
  }

  #handleNextStep() {
    if (this.currentStepIndex >= this.steps.length - 1) return;
    if (!this.isStepComplete(this.currentStepIndex)) {
      this.#highlightMissingFields(this.currentStepIndex);
      return;
    }
    this.goToStep(this.currentStepIndex + 1);
  }

  goToStep(index) {
    if (index < 0 || index >= this.steps.length) return;

    // Collapse the summary from step 3 onwards on mobile
    if (index >= 2 && window.innerWidth < 750) {
      this.summaryExpanded = false;
    }

    const container = this.refs.fieldsContainer;
    const current = container.querySelector(`[data-step-index="${this.currentStepIndex}"]`);
    const target = container.querySelector(`[data-step-index="${index}"]`);

    current?.classList.add('is-hidden');
    current?.classList.remove('is-entering-left', 'is-entering-right');

    if (target) {
      target.classList.remove('is-hidden');
      target.classList.add(index > this.currentStepIndex ? 'is-entering-right' : 'is-entering-left');
      setTimeout(() => target.classList.remove('is-entering-left', 'is-entering-right'), 300);
    }

    this.currentStepIndex = index;
    this.visitedSteps.add(index);
    container.scrollTop = 0;

    this.#refresh();
  }

  #updateStepStates() {
    const { progress } = this.refs;
    if (!progress) return;

    progress.querySelectorAll('.personaliser__progress-item').forEach((item, index) => {
      item.classList.remove('is-current', 'is-complete', 'is-incomplete');
      if (index === this.currentStepIndex) {
        item.classList.add('is-current');
      } else if (this.visitedSteps.has(index) && this.isStepComplete(index)) {
        item.classList.add('is-complete');
      } else {
        item.classList.add('is-incomplete');
      }
    });

    progress.querySelectorAll('.personaliser__progress-connector').forEach((connector, index) => {
      connector.classList.toggle(
        'is-complete',
        this.visitedSteps.has(index) && this.isStepComplete(index)
      );
    });
  }

  #updateStepNavigation() {
    const { addButton, readyMessage } = this.refs;
    if (!addButton) return;

    const allComplete = this.#allRequiredFieldsComplete();
    const isLastStep = this.currentStepIndex === this.steps.length - 1;
    addButton.style.display = allComplete || isLastStep ? '' : 'none';

    // "You're all set!" is tied to Add to Basket actually being usable:
    // every required (visible) field is complete.
    const ready = allComplete;
    if (readyMessage) readyMessage.hidden = !ready;

    if (ready) {
      addButton.classList.remove('is-pulsing');
      void addButton.offsetWidth;
      addButton.classList.add('is-pulsing');
    } else {
      addButton.classList.remove('is-pulsing');
    }
  }

  #highlightMissingFields(stepIndex) {
    const step = this.steps[stepIndex];
    const stepEl = this.refs.fieldsContainer?.querySelector(`[data-step-index="${stepIndex}"]`);
    if (!step || !stepEl) return;

    for (const field of this.#fieldsForStep(step)) {
      const wrapper = stepEl.querySelector(`[data-field-key="${field.key}"]`);
      if (!wrapper) continue;

      const isInvalid = !this.#fieldComplete(field);
      wrapper.classList.toggle('is-invalid', isInvalid);

      if (isInvalid) {
        const input = wrapper.querySelector('input, select, textarea, button');
        const clear = () => {
          wrapper.classList.remove('is-invalid');
          input?.removeEventListener('change', clear);
          input?.removeEventListener('input', clear);
        };
        input?.addEventListener('change', clear);
        input?.addEventListener('input', clear);
      }
    }
  }

  // ==========================================================================
  // Field creation
  // ==========================================================================

  #createField(field) {
    const wrapper = document.createElement('div');
    wrapper.className = `personaliser__field personaliser__field--${field.type}`;
    wrapper.dataset.fieldKey = field.key;

    if (field.show_if) {
      wrapper.hidden = true;
      wrapper.dataset.visible = 'false';
    }

    const builders = {
      text: this.#createTextField,
      select: field.swatches ? this.#createSwatchSelectField : this.#createSelectField,
      variant_select: this.#createVariantSelectField,
      checkbox: this.#createCheckboxField,
      image: this.#createImageField,
    };

    const builder = builders[field.type];
    if (!builder) return null;
    builder.call(this, field, wrapper);

    if (field.help_text && field.type !== 'text') {
      const help = document.createElement('p');
      help.className = 'personaliser__help-text';
      help.textContent = field.help_text;
      wrapper.appendChild(help);
    }

    return wrapper;
  }

  #fieldLabel(field, extras = '') {
    const label = document.createElement('label');
    label.className = 'form__label';
    label.innerHTML = `
      <span>${escapeHtml(field.label)}</span>
      ${field.required ? `<span class="required">(${escapeHtml(this.t.required || 'Required')})</span>` : ''}
      ${extras}
    `;
    return label;
  }

  // --- Text -----------------------------------------------------------------

  #createTextField(field, wrapper) {
    const maxLength = field.max_length || 100;
    // Priced text fields always show their cost — for engraving sides
    // (price_when: 'filled') this makes it clear EACH side adds the fee.
    const priceExtra =
      field.price > 0
        ? `<span class="price-addition">+${this.formatMoney(field.price * 100)}</span>`
        : '';
    // Price badge sits directly after the field name; char count stays right
    const label = document.createElement('label');
    label.className = 'form__label';
    label.innerHTML = `
      <span>${escapeHtml(field.label)}</span>
      ${priceExtra}
      ${field.required ? `<span class="required">(${escapeHtml(this.t.required || 'Required')})</span>` : ''}
      <span class="personaliser__char-count" data-char-count="${field.key}">0/${maxLength}</span>
    `;

    const inputWrapper = document.createElement('div');
    inputWrapper.className = 'personaliser__input-wrapper';

    const textarea = document.createElement('textarea');
    textarea.className = 'field__input personaliser__textarea';
    textarea.placeholder = field.placeholder || '';
    textarea.maxLength = maxLength;
    textarea.rows = maxLength > 100 ? 3 : 1;
    if (field.default_font) textarea.style.fontFamily = field.default_font;

    textarea.addEventListener('input', () => {
      this.#applyFontConstraints(field, textarea);
      this.selections[field.key] = textarea.value;
      this.#updateCharCount(field, textarea);
      this.#refresh();
    });

    inputWrapper.appendChild(textarea);

    if (field.font_selector && this.#selectableFonts.length) {
      inputWrapper.appendChild(this.#createFontSelect(field, textarea, wrapper));
    }

    if (field.help_text) {
      const help = document.createElement('p');
      help.className = 'personaliser__help-text';
      help.textContent = field.help_text;
      inputWrapper.appendChild(help);
    }

    wrapper.appendChild(label);
    wrapper.appendChild(inputWrapper);
  }

  #createFontSelect(field, textarea, wrapper) {
    const select = document.createElement('select');
    select.className = 'personaliser__select';
    select.innerHTML = `<option value="">${escapeHtml(this.t.selectFont || 'Select a font')}</option>`;

    for (const font of this.#selectableFonts) {
      const option = document.createElement('option');
      option.setAttribute('value', font);
      option.textContent = font.replace(/-/g, ' ');
      option.style.fontFamily = FONT_BEHAVIOURS[font]?.dropdownFamily || font;
      select.appendChild(option);
    }

    select.addEventListener('change', () => {
      const font = select.value;
      this.selections[`${field.key}_font`] = font || field.default_font || '';
      select.style.fontFamily = FONT_BEHAVIOURS[font]?.dropdownFamily || font || 'inherit';
      textarea.style.fontFamily =
        FONT_BEHAVIOURS[font]?.dropdownFamily || font || field.default_font || 'inherit';

      // Special-font limits (monogram/interlocking): keep/restore original text
      const behaviour = FONT_BEHAVIOURS[font];
      if (behaviour && !textarea.dataset.originalText) {
        textarea.dataset.originalText = textarea.value;
      } else if (!behaviour && textarea.dataset.originalText) {
        textarea.value = textarea.dataset.originalText;
        delete textarea.dataset.originalText;
        this.selections[field.key] = textarea.value;
      }

      this.#applyFontConstraints(field, textarea);
      this.selections[field.key] = textarea.value;
      this.#updateCharCount(field, textarea);
      this.#updateFontMessage(field, wrapper, font);
      this.#refresh();
    });

    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'personaliser__select-wrapper';
    selectWrapper.appendChild(select);
    selectWrapper.appendChild(caretSvg());
    return selectWrapper;
  }

  #updateFontMessage(field, wrapper, font) {
    let message = wrapper.querySelector('.personaliser__font-message');
    if (FONT_BEHAVIOURS[font]) {
      if (!message) {
        message = document.createElement('p');
        message.className = 'personaliser__font-message';
        wrapper.appendChild(message);
      }
      message.textContent = this.t.fontLimitMessage || 'Maximum 3 characters for this font.';
      message.hidden = false;
    } else if (message) {
      message.hidden = true;
    }
  }

  /** Enforce per-font constraints on the textarea value. */
  #applyFontConstraints(field, textarea) {
    const font = this.selections[`${field.key}_font`];
    const behaviour = FONT_BEHAVIOURS[font];
    let text = textarea.value;

    if (behaviour) {
      if (behaviour.noWhitespace && /\s/.test(text)) text = text.replace(/\s/g, '');
      if (text.length > behaviour.maxLength) text = text.slice(0, behaviour.maxLength);
    } else {
      const maxLength = field.max_length || 100;
      if (text.length > maxLength) text = text.slice(0, maxLength);
    }

    if (text !== textarea.value) textarea.value = text;
  }

  #updateCharCount(field, textarea) {
    const el = this.querySelector(`[data-char-count="${field.key}"]`);
    if (!el) return;
    const font = this.selections[`${field.key}_font`];
    const max = FONT_BEHAVIOURS[font]?.maxLength ?? (field.max_length || 100);
    el.textContent = `${textarea.value.length}/${max}`;
  }

  // --- Select ----------------------------------------------------------------

  #optionPriceText(option) {
    return option.price > 0 ? ` (+${this.formatMoney(option.price * 100)})` : '';
  }

  #createSelectField(field, wrapper) {
    const label = this.#fieldLabel(field);
    const select = document.createElement('select');
    select.className = 'personaliser__select';

    if (!field.default_value) {
      select.innerHTML = `<option value="">${escapeHtml(field.placeholder || this.t.selectPlaceholder || 'Select an option')}</option>`;
    }

    for (const option of field.options || []) {
      const el = document.createElement('option');
      el.setAttribute('value', option.value);
      el.textContent = option.label + this.#optionPriceText(option);
      if (field.default_value === option.value) el.setAttribute('selected', '');
      select.appendChild(el);
    }

    select.addEventListener('change', () => {
      const option = (field.options || []).find((o) => o.value === select.value);
      if (option) {
        this.selections[field.key] = {
          value: option.value,
          label: option.label,
          label_internal: option.label_internal || option.label,
          price: option.price || 0,
        };
      } else {
        delete this.selections[field.key];
      }
      this.#refresh();
    });

    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'personaliser__select-wrapper';
    selectWrapper.appendChild(select);
    selectWrapper.appendChild(caretSvg());

    wrapper.appendChild(label);
    wrapper.appendChild(selectWrapper);
  }

  // --- Swatch select ----------------------------------------------------------

  #swatchColor(value) {
    const normalised = value.toLowerCase().replace(/[\s-]+/g, '_');
    for (const [pattern, color] of SWATCH_COLORS) {
      if (pattern.test(normalised)) return color;
    }
    return 'transparent';
  }

  #swatchOptionHtml(option) {
    return `
      <span class="personaliser__swatch-color" style="background-color: ${this.#swatchColor(option.value)};"></span>
      <span class="personaliser__swatch-label">${escapeHtml(option.label)}${this.#optionPriceText(option)}</span>
    `;
  }

  #createSwatchSelectField(field, wrapper) {
    const label = this.#fieldLabel(field);
    const container = document.createElement('div');
    container.className = 'personaliser__swatch-select';

    const selected = document.createElement('button');
    selected.type = 'button';
    selected.className = 'personaliser__swatch-selected';

    const dropdown = document.createElement('div');
    dropdown.className = 'personaliser__swatch-dropdown';

    const setSelected = (option) => {
      this.selections[field.key] = {
        value: option.value,
        label: option.label,
        label_internal: option.label_internal || option.label,
        price: option.price || 0,
      };
      selected.innerHTML = this.#swatchOptionHtml(option);
      selected.appendChild(caretSvg());
      dropdown.querySelectorAll('.personaliser__swatch-option').forEach((el) => {
        el.classList.toggle('is-selected', el.dataset.value === option.value);
      });
    };

    for (const option of field.options || []) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'personaliser__swatch-option';
      el.dataset.value = option.value;
      el.innerHTML = this.#swatchOptionHtml(option);
      el.addEventListener('click', () => {
        setSelected(option);
        container.classList.remove('is-open');
        this.#refresh();
      });
      dropdown.appendChild(el);
    }

    const defaultOption = field.default_value
      ? (field.options || []).find((o) => o.value === field.default_value)
      : (field.options || [])[0];
    if (defaultOption) setSelected(defaultOption);

    selected.addEventListener('click', () => container.classList.toggle('is-open'));
    document.addEventListener(
      'click',
      (e) => {
        if (!container.contains(/** @type {Node} */ (e.target))) {
          container.classList.remove('is-open');
        }
      },
      { signal: this.#abortController.signal }
    );

    container.appendChild(selected);
    container.appendChild(dropdown);
    wrapper.appendChild(label);
    wrapper.appendChild(container);
  }

    // --- Variant select ----------------------------------------------------------

  /**
   * Resolves the add-on variant for an option. Options carry either a flat
   * `variant_id`, or a `variant_ids` map keyed by the value of the field named
   * in the field's `variant_from` (e.g. the chain in the chosen metal). Empty
   * string = no variant exists for the current parent selection.
   */
  #optionVariantId(field, option) {
    if (field.variant_from && option.variant_ids) {
      const source = this.selections[field.variant_from];
      const key = source && typeof source === 'object' ? source.value : source;
      return key ? String(option.variant_ids[key] || '') : '';
    }
    return String(option.variant_id || '');
  }

  /**
   * 'ok' | 'unavailable' | 'out_of_stock'. An option with no variant at all
   * (e.g. "Standard Jump Ring" on an attachment select) is a valid "none"
   * choice and stays 'ok'; 'unavailable' is only for a variant_ids map with
   * no entry for the current parent selection.
   */
  #optionAvailability(field, option) {
    const variantId = this.#optionVariantId(field, option);
    if (!variantId) return field.variant_from && option.variant_ids ? 'unavailable' : 'ok';
    if (this.addonStock[variantId] === false) return 'out_of_stock';
    return 'ok';
  }

  /** Re-labels and enables/disables a variant select's options in place. */
  #syncVariantOptions(field, select) {
    for (const el of select.options) {
      if (!el.value) continue; // placeholder
      const option = (field.options || []).find((o) => o.value === el.value);
      if (!option) continue;

      const availability = this.#optionAvailability(field, option);
      let text = option.label + this.#optionPriceText(option);
      if (availability === 'out_of_stock') text += ` - ${this.t.outOfStock || 'Out of stock'}`;
      if (availability === 'unavailable') text += ` - ${this.t.notAvailable || 'Not available'}`;

      el.textContent = text;
      el.disabled = availability !== 'ok';
    }
  }

  /**
   * Variant selects with `variant_from` follow their parent field: re-sync the
   * dropdown, re-point the stored variant_id, and drop a selection that no
   * longer resolves (18+2 chosen in silver, then metal switched to rose gold).
   * Runs on every refresh so it also catches changes made on an earlier step.
   */
  #updateVariantSelections() {
    for (const field of this.config.fields || []) {
      if (field.type !== 'variant_select' || !field.variant_from) continue;

      const select = this.refs.fieldsContainer?.querySelector(
        `[data-field-key="${field.key}"] select`
      );
      if (!select) continue;

      this.#syncVariantOptions(field, select);

      const sel = this.selections[field.key];
      if (!sel?.value) continue;

      const option = (field.options || []).find((o) => o.value === sel.value);
            if (option && this.#optionAvailability(field, option) === 'ok') {
        sel.variant_id = this.#optionVariantId(field, option) || undefined;
      } else {
        delete this.selections[field.key];
        select.value = '';
      }
    }
  }

  #createVariantSelectField(field, wrapper) {
    const label = this.#fieldLabel(field);
    const select = document.createElement('select');
    select.className = 'personaliser__select';
    select.innerHTML = `<option value="">${escapeHtml(field.placeholder || this.t.selectPlaceholder || 'Select an option')}</option>`;

    for (const option of field.options || []) {
      const el = document.createElement('option');
      el.setAttribute('value', option.value);
      select.appendChild(el);
    }
    // Labels + disabled state depend on stock and (with variant_from) on the
    // parent selection, so they live in one place and are re-run on refresh.
    this.#syncVariantOptions(field, select);

        select.addEventListener('change', () => {
      const option = (field.options || []).find((o) => o.value === select.value);

      if (option && this.#optionAvailability(field, option) === 'ok') {
        this.selections[field.key] = {
          value: option.value,
          label: option.label,
          label_internal: option.label_internal || option.label,
          variant_id: this.#optionVariantId(field, option) || undefined,
          price: option.price || 0,
          zero_price: field.zero_price || false,
          type: 'variant_select',
        };
      } else {
        delete this.selections[field.key];
        select.value = '';
      }
      this.#refresh();
    });

    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'personaliser__select-wrapper';
    selectWrapper.appendChild(select);
    selectWrapper.appendChild(caretSvg());

    wrapper.appendChild(label);
    wrapper.appendChild(selectWrapper);
  }

  // --- Checkbox ------------------------------------------------------------------

  #createCheckboxField(field, wrapper) {
    const label = document.createElement('label');
    label.className = 'personaliser__checkbox-label';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';

    checkbox.addEventListener('change', () => {
      this.selections[field.key] = {
        checked: checkbox.checked,
        value: checkbox.checked ? 'yes' : 'no',
        label: field.label,
        label_internal: field.label_internal || field.label,
        price: checkbox.checked ? field.price || 0 : 0,
      };
      this.#refresh();
    });

    const span = document.createElement('span');
    span.textContent =
      field.label + (field.price > 0 ? ` (+${this.formatMoney(field.price * 100)})` : '');

    label.appendChild(checkbox);
    label.appendChild(span);
    wrapper.appendChild(label);
  }

  // --- Image upload -----------------------------------------------------------------

  #hasImageFields() {
    return (this.config.fields || []).some((f) => f.type === 'image');
  }

  #createImageField(field, wrapper) {
    const priceExtra =
      field.price > 0 ? `<span class="price-addition">+${this.formatMoney(field.price * 100)}</span>` : '';
    const label = this.#fieldLabel(field, priceExtra);

    const uploadWrapper = document.createElement('div');
    uploadWrapper.className = 'personaliser__input-wrapper';

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.hidden = true;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.textContent = this.t.uploadImage || 'Upload image';
    button.addEventListener('click', () => input.click());

    const status = document.createElement('p');
    status.className = 'personaliser__upload-status';

    const preview = document.createElement('div');
    preview.className = 'personaliser__image-preview';
    preview.hidden = true;

    input.addEventListener('change', () =>
      this.#handleImageUpload(field, input, button, status, preview)
    );

    uploadWrapper.append(input, button, status, preview);
    wrapper.appendChild(label);
    wrapper.appendChild(uploadWrapper);
  }

  async #handleImageUpload(field, input, button, status, preview) {
    const file = input.files?.[0];
    if (!file) return;

    const maxMb = field.max_size_mb || 5;
    if (file.size > maxMb * 1024 * 1024) {
      status.textContent = (this.t.imageTooLarge || 'File too large. Max: {max}MB').replace('{max}', String(maxMb));
      status.className = 'personaliser__upload-status error';
      return;
    }

    status.textContent = this.t.uploading || 'Uploading…';
    status.className = 'personaliser__upload-status uploading';
    button.disabled = true;

    try {
      const token = await this.#getTurnstileToken();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('cf_turnstile_token', token);

      const response = await fetch(this.context.uploadUrl, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
      const data = await response.json();

      this.selections[field.key] = {
        url: data.url,
        filename: data.filename,
        key: data.key,
        price: field.price || 0,
        label_internal: field.label_internal || field.label,
      };

      preview.innerHTML = `<img src="${escapeHtml(data.url)}" alt="">`;
      preview.hidden = false;
      status.textContent = this.t.uploadComplete || 'Upload complete';
      status.className = 'personaliser__upload-status success';
      this.#refresh();
    } catch (err) {
      console.error('[personaliser] upload error:', err);
      status.textContent = this.t.uploadFailed || 'Upload failed. Please try again.';
      status.className = 'personaliser__upload-status error';
    } finally {
      if (window.turnstile && this.#turnstileWidgetId !== null) {
        try {
          window.turnstile.reset(this.#turnstileWidgetId);
        } catch {
          /* noop */
        }
      }
      button.disabled = false;
    }
  }

  // --- Turnstile (lazy) ---------------------------------------------------------------

  #loadTurnstileScript() {
    if (window.turnstile || document.querySelector(`script[src^="${TURNSTILE_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    document.head.appendChild(script);
  }

  async #getTurnstileToken() {
    this.#loadTurnstileScript();

    // Wait for the turnstile API
    await new Promise((resolve, reject) => {
      const start = Date.now();
      const timer = setInterval(() => {
        if (window.turnstile) {
          clearInterval(timer);
          resolve(undefined);
        } else if (Date.now() - start > 10000) {
          clearInterval(timer);
          reject(new Error('Turnstile failed to load'));
        }
      }, 50);
    });

    if (this.#turnstileWidgetId === null) {
      this.#turnstileWidgetId = window.turnstile.render(this.refs.turnstileContainer, {
        sitekey: this.context.turnstileSitekey,
        size: 'invisible',
        callback: (token) => {
          this.#turnstileToken = token;
        },
      });
    }

    this.#turnstileToken = null;
    try {
      window.turnstile.execute(this.#turnstileWidgetId, { action: 'engraving_upload' });
    } catch {
      window.turnstile.reset(this.#turnstileWidgetId);
      window.turnstile.execute(this.#turnstileWidgetId, { action: 'engraving_upload' });
    }

    return new Promise((resolve, reject) => {
      const start = Date.now();
      const timer = setInterval(() => {
        if (this.#turnstileToken) {
          clearInterval(timer);
          resolve(this.#turnstileToken);
        } else if (Date.now() - start > 8000) {
          clearInterval(timer);
          reject(new Error('Failed to obtain Turnstile token'));
        }
      }, 50);
    });
  }

  // ==========================================================================
  // Pricing
  // ==========================================================================

  #updatePricing() {
    this.priceAdditions = 0;

    for (const field of this.config.fields || []) {
      if (!this.isFieldVisible(field)) continue;
      const sel = this.selections[field.key];
      if (!sel) continue;

      if (field.type === 'text' && field.price > 0 && typeof sel === 'string' && sel.trim()) {
        this.priceAdditions += field.price;
      } else if (
        (field.type === 'select' || field.type === 'variant_select') &&
        sel.price > 0
      ) {
        this.priceAdditions += sel.price;
      } else if (field.type === 'checkbox' && sel.checked && sel.price > 0) {
        this.priceAdditions += sel.price;
      } else if (field.type === 'image' && sel.url && sel.price > 0) {
        this.priceAdditions += sel.price;
      }
    }

    const { additionsRow, additionsPrice, totalPrice } = this.refs;

    // Upgrades are separate cart lines — shown in the modal total but NEVER
    // part of price_additions (the Cart Transform would double-charge them).
    const upgradesMinor = this.#upgradesTotalMinor;
    this.#renderUpgradeRows();

    if (additionsRow) additionsRow.hidden = this.priceAdditions <= 0;
    if (additionsPrice) additionsPrice.textContent = this.formatMoney(this.priceAdditions * 100);
    if (totalPrice) {
      totalPrice.textContent = this.formatMoney(
        this.basePriceMinor + this.priceAdditions * 100 + upgradesMinor
      );
    }
  }

  // ==========================================================================
  // Previews
  // ==========================================================================

  #renderPreviewPanel() {
    this.previewFields = (this.config.fields || []).filter(
      (f) => f.type === 'text' && f.show_preview !== false
    );

    const container = this.refs.previewPanel;
    if (!container) return;

    let html = `
      <div class="personaliser__summary-wrapper">
        <button type="button" class="personaliser__summary-toggle" data-role="summary-toggle">
          <span data-role="summary-toggle-text">${escapeHtml(this.t.yourSelections || 'Your selections')}</span>
          ${caretSvg().outerHTML}
        </button>
        <div class="personaliser__summary" data-role="selection-summary"></div>
      </div>
    `;

    if (this.previewFields.length) {
      html += '<div class="personaliser__previews">';
      for (const field of this.previewFields) {
        html += `
          <div class="personaliser__preview-item" data-preview-item="${field.key}" hidden>
            <span class="personaliser__preview-label">${escapeHtml(field.label)}</span>
            <div class="personaliser__preview" data-preview-for="${field.key}">
              <span class="preview-text"></span>
            </div>
          </div>
        `;
      }
      html += '</div>';
      html += `<p class="personaliser__preview-caption" hidden>${escapeHtml(this.t.previewCaption || 'Preview for illustration purposes only.')}</p>`;
    }

    container.innerHTML = html;

    container.querySelector('[data-role="summary-toggle"]')?.addEventListener('click', () => {
      this.summaryExpanded = !this.summaryExpanded;
      this.#updateSummaryExpandedState();
    });
  }

  #previewStyleClass(field) {
    let metal = null;
    if (field.preview_style_from) {
      const source = this.selections[field.preview_style_from];
      if (source?.value) metal = METAL_STYLE_MAP[source.value] || null;
    }
    if (!metal) metal = field.default_preview_style || null;
    if (!metal) return null;

    // Cut-out variant emits cutout-{metal}--{font}; engraved emits bare metal.
    if (field.preview_variant) {
      const font = this.selections[`${field.key}_font`] || field.default_font;
      const fontSlug = font ? font.toLowerCase().replace(/\s+/g, '-') : '';
      return fontSlug
        ? `${field.preview_variant}-${metal}--${fontSlug}`
        : `${field.preview_variant}-${metal}`;
    }

    return metal;
  }

  /** Compose the preview HTML for a text + font combination. */
  #previewHtml(text, font) {
    if (font === 'round-monogram') {
      const chars = (text || 'Exa').slice(0, 3);
      const families =
        chars.length === 1
          ? ['round-monogram-l']
          : chars.length === 2
          ? ['round-monogram-l', 'round-monogram-r']
          : ['round-monogram-l', 'round-monogram-c', 'round-monogram-r'];
      return [...chars]
        .map(
          (char, i) =>
            `<span class="preview-text" style="font-family: ${families[i]}; font-size: 70px;">${escapeHtml(char)}</span>`
        )
        .join('');
    }

    if (font === 'interlocking') {
      let display = text || 'eXa';
      if (text) {
        display =
          text.length === 3
            ? text.charAt(0).toLowerCase() + text.charAt(1).toUpperCase() + text.charAt(2).toLowerCase()
            : text.toUpperCase();
      }
      return `<span class="preview-text" style="font-family: interlocking; font-size: 70px;">${escapeHtml(display)}</span>`;
    }

    const display = text || this.t.example || 'Example';
    return `<span class="preview-text" style="font-family: ${font || 'inherit'}; font-size: ${previewFontSize(display)};">${escapeHtml(display).replace(/\n/g, '<br>')}</span>`;
  }

  #updatePreviews() {
    if (!this.previewFields?.length) return;

    let anyVisible = false;

    for (const field of this.previewFields) {
      const item = this.querySelector(`[data-preview-item="${field.key}"]`);
      const preview = this.querySelector(`[data-preview-for="${field.key}"]`);
      if (!item || !preview) continue;

      const text = this.selections[field.key];
      const hasText = typeof text === 'string' && text.trim().length > 0;

      let onCurrentStep = true;
      if (this.layoutMode === 'steps') {
        onCurrentStep = this.#stepIndexForField(field) === this.currentStepIndex;
      }

      const show = this.isFieldVisible(field) && hasText && onCurrentStep;
      item.hidden = !show;
      if (!show) continue;

      anyVisible = true;
      const font = this.selections[`${field.key}_font`] || field.default_font || '';
      preview.className = 'personaliser__preview';
      const styleClass = this.#previewStyleClass(field);
      if (styleClass) preview.classList.add(`personaliser__preview--${styleClass}`);
      preview.innerHTML = this.#previewHtml(text, font);
    }

    const caption = this.querySelector('.personaliser__preview-caption');
    if (caption) caption.hidden = !anyVisible;
  }

  /** Marks the preview column empty when neither summary nor previews show. */
  #updatePreviewPanelState() {
    const panel = this.refs.previewPanel;
    if (!panel) return;

    const anyPreview = panel.querySelector('[data-preview-item]:not([hidden])');
    const summaryWrapper = panel.querySelector('.personaliser__summary-wrapper');
    const summaryContent = panel.querySelector('[data-role="selection-summary"]');
    const summaryShown =
      summaryWrapper &&
      summaryWrapper.style.display !== 'none' &&
      summaryContent &&
      summaryContent.innerHTML.trim() !== '';

    panel.classList.toggle('is-empty', !anyPreview && !summaryShown);
  }

  #updateSummaryExpandedState() {
    this.querySelector('.personaliser__summary-wrapper')?.classList.toggle(
      'is-collapsed',
      !this.summaryExpanded
    );
  }

  #updateSelectionSummary() {
    const container = this.querySelector('[data-role="selection-summary"]');
    if (!container) return;

    const items = [];

    for (const field of this.config.fields || []) {
      if (!this.isFieldVisible(field)) continue;

      if (this.layoutMode === 'steps') {
        const stepIndex = this.#stepIndexForField(field);
        if (stepIndex === -1 || stepIndex === this.currentStepIndex) continue;
        if (!this.visitedSteps.has(stepIndex) || !this.isStepComplete(stepIndex)) continue;
      }

      const sel = this.selections[field.key];
      if (!sel) continue;

      if (field.type === 'select' || field.type === 'variant_select') {
        if (sel.value && sel.value.toLowerCase() !== 'no') {
          items.push({
            label: cleanLabel(field.section_label || field.label),
            value: sel.label || sel.value,
            price: sel.price,
          });
        }
      } else if (field.type === 'text' && typeof sel === 'string' && sel.trim()) {
        items.push({
          label: cleanLabel(field.label),
          value: sel.length > 20 ? `${sel.substring(0, 20)}…` : sel,
        });
        const font = this.selections[`${field.key}_font`];
        if (font && field.font_selector) {
          items.push({ label: this.t.font || 'Font', value: font });
        }
      } else if (field.type === 'checkbox' && sel.checked) {
        items.push({ label: field.label, value: this.t.yes || 'Yes', price: sel.price });
      } else if (field.type === 'image' && sel.url) {
        items.push({ label: field.label, value: this.t.imageUploaded || 'Image uploaded', price: sel.price });
      }
    }

    const wrapper = this.querySelector('.personaliser__summary-wrapper');
    if (wrapper && this.layoutMode === 'steps') {
      wrapper.style.display = this.currentStepIndex === 0 ? 'none' : '';
    }

    const toggleText = this.querySelector('[data-role="summary-toggle-text"]');
    if (toggleText) {
      const base = this.t.yourSelections || 'Your selections';
      toggleText.textContent = items.length ? `${base} (${items.length})` : base;
    }

    container.innerHTML = items
      .map(
        (item) => `
          <div class="personaliser__summary-row">
            <span class="personaliser__summary-label">${escapeHtml(item.label)}</span>
            <span class="personaliser__summary-value">
              ${escapeHtml(String(item.value))}
              ${item.price ? `<span class="personaliser__summary-price">+${this.formatMoney(item.price * 100)}</span>` : ''}
            </span>
          </div>
        `
      )
      .join('');

    this.#updateSummaryExpandedState();
  }

  // ==========================================================================
  // Cart properties
  // ==========================================================================

  get #isEngravingMode() {
    return this.config.pricing_mode === 'engraving_sides';
  }

  /** @param {string} name @param {string} value */
  #propertyInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    input.setAttribute('form', this.context.formId);
    return input;
  }

  /**
   * Writes ALL personalisation line-item property inputs. Inputs live in the
   * component's bucket and reference the main product form via the `form`
   * attribute, so Horizon's native ProductFormComponent submits them.
   *
   * IMPORTANT: only called during the personaliser's own submit, and the
   * bucket is cleared straight after — the standard Add to Basket must add a
   * plain, non-personalised product.
   */
  #writeCartProperties() {
    const bucket = this.refs.propertiesBucket;
    if (!bucket) return;

    const inputs = [];

    if (this.#isEngravingMode) {
      let sidesCount = 0;

      for (const field of this.config.fields || []) {
        if (field.type !== 'text') continue;
        const text = this.selections[field.key];
        if (typeof text !== 'string' || !text.trim()) continue;

        if (field.engraving_side) {
          sidesCount += 1;
          const font = this.selections[`${field.key}_font`] || '';
          const hiddenTextKey = `_personalised_${field.key}_TEXT`;
          const hiddenFontKey = `_personalised_${field.key}_FONT`;
          inputs.push(this.#propertyInput(hiddenTextKey, text));
          if (font) inputs.push(this.#propertyInput(hiddenFontKey, font));

          // Human-readable copies, matching the legacy interceptor naming:
          // "_personalised_FRONT_ENGRAVE_TEXT" → "Front Engrave Text"
          const visible = (key) =>
            key
              .replace('_personalised_', '')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase())
              .replace(/\B\w+/g, (w) => w.toLowerCase());
          inputs.push(this.#propertyInput(visible(hiddenTextKey), text));
          if (font) inputs.push(this.#propertyInput(visible(hiddenFontKey), font));
        } else if (field.cart_property) {
          inputs.push(this.#propertyInput(field.cart_property, text));
        }
      }

      if (sidesCount > 0) {
        inputs.push(this.#propertyInput('_engraving_sides_count', String(sidesCount)));
        inputs.push(this.#propertyInput('_personalisation', 'true'));
      }
    } else {
      // Advanced: machine-readable JSON payload (read by the Cart Transform)
      const cleanSelections = {};
      for (const key of Object.keys(this.selections)) {
        const baseKey = key.endsWith('_font') ? key.replace('_font', '') : key;
        const baseField = (this.config.fields || []).find((f) => f.key === baseKey);
        if (baseField && !this.isFieldVisible(baseField)) continue;

        const value = this.selections[key];
        if (typeof value === 'object' && value !== null) {
          const copy = { ...value };
          if (copy.label_internal) {
            copy.label = copy.label_internal;
            delete copy.label_internal;
          }
          cleanSelections[key] = copy;
        } else if (typeof value === 'string' && value.trim()) {
          cleanSelections[key] = value.trim();
        }
      }

      inputs.push(
        this.#propertyInput(
          '_personalisation_data',
          JSON.stringify({
            selections: cleanSelections,
            price_additions: this.priceAdditions,
            config_handle: this.config.handle || 'unknown',
          })
        )
      );

      // Human-readable properties for ops / order display
      for (const field of this.config.fields || []) {
        if (!this.isFieldVisible(field)) continue;
        const value = this.selections[field.key];
        if (!value) continue;

        if (field.type === 'image' && value.url) {
          // Admin-facing property names stay English: prefer label_internal.
          let imageLabel = field.label_internal || field.label;
          if (field.show_if) {
            const parent = (this.config.fields || []).find((f) => f.key === field.show_if.field);
            if (parent) {
              const parentLabel = parent.label_internal || parent.label;
              imageLabel = parentLabel.replace('Choose ', '').replace('Select ', '');
            }
          }
          inputs.push(this.#propertyInput(imageLabel, value.url));
          continue;
        }

        let displayValue = '';
        if (field.type === 'text' && typeof value === 'string' && value.trim()) {
          displayValue = value;
        } else if ((field.type === 'select' || field.type === 'variant_select') && value.value) {
          displayValue = value.label_internal || value.label || value.value;
        } else if (field.type === 'checkbox' && value.checked) {
          displayValue = 'Yes';
        }

        if (displayValue) {
          // "Method" selects (with a dependent image field) are hidden from
          // the customer with a leading underscore, as before.
          const isMethodSelect =
            field.type === 'select' &&
            (this.config.fields || []).some(
              (f) => f.type === 'image' && f.show_if?.field === field.key
            );
          const fieldLabel = field.label_internal || field.label;
          const name = isMethodSelect ? `_${cleanLabel(fieldLabel)}` : cleanLabel(fieldLabel);
          inputs.push(this.#propertyInput(name, displayValue));
        }

        if (field.font_selector && this.selections[`${field.key}_font`]) {
          inputs.push(
            this.#propertyInput(`${field.label_internal || field.label} Font`, this.selections[`${field.key}_font`])
          );
        }
      }
    }

    bucket.replaceChildren(...inputs);
  }

  // ==========================================================================
  // Submit
  // ==========================================================================

  /** Fall back to "email later" when an upload trigger was chosen but nothing uploaded. */
  #handleIncompleteUploads() {
    for (const imageField of (this.config.fields || []).filter((f) => f.type === 'image')) {
      if (!imageField.show_if) continue;

      const parentKey = imageField.show_if.field;
      const parentSelection = this.selections[parentKey];
      if (!parentSelection || parentSelection.value !== imageField.show_if.value) continue;
      if (this.selections[imageField.key]?.url) continue;

      const parentField = (this.config.fields || []).find((f) => f.key === parentKey);
      const emailLater = parentField?.options?.find(
        (o) => o.value === 'email_later' || o.value.includes('email')
      );
      if (emailLater) {
        this.selections[parentKey] = {
          value: emailLater.value,
          label: emailLater.label,
          label_internal: emailLater.label_internal || emailLater.label,
          price: emailLater.price || 0,
        };
      }
    }
  }

  #validate() {
    if (this.#isEngravingMode) {
      const sideFields = (this.config.fields || []).filter((f) => f.engraving_side);
      const filled = sideFields.filter((f) => {
        const text = this.selections[f.key];
        return typeof text === 'string' && text.trim();
      });

      const missingFont = filled.filter((f) => !this.selections[`${f.key}_font`]);
      if (missingFont.length === 1) {
        return (this.t.chooseFontForSide || 'Please choose a font for {side}.').replace(
          '{side}',
          missingFont[0].label_internal || missingFont[0].label
        );
      }
      if (missingFont.length > 1) {
        return this.t.chooseFontForAllSides || 'Please choose a font for every engraved side.';
      }

      const minCompleted = this.config.validation?.min_completed || 0;
      if (filled.length < minCompleted) {
        return this.t.chooseFontAndText || 'Please enter your engraving text and choose a font.';
      }
      return '';
    }

    const missing = (this.config.fields || [])
      .filter((field) => field.required && this.isFieldVisible(field) && !this.#fieldComplete(field))
      .map((field) => field.label);

    if (missing.length) {
      return `${this.t.completeFields || 'Please complete'}: ${missing.join(', ')}`;
    }
    return '';
  }

  /** Quantity the main form is about to add — add-on lines must match it. */
  get #mainQuantity() {
    const form = this.#mainForm;
    if (!form) return 1;
    const quantity = Math.floor(Number(new FormData(form).get('quantity')));
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #variantAddons() {
    const addons = [];
    const mainSku = this.#currentVariant.sku || '';
    const quantity = this.#mainQuantity;

    for (const field of (this.config.fields || []).filter((f) => f.type === 'variant_select')) {
      if (!this.isFieldVisible(field)) continue;
      const sel = this.selections[field.key];
      if (!sel?.variant_id) continue;

      addons.push({
        id: sel.variant_id,
        quantity,
        properties: {
          _zero_price_addon: sel.zero_price ? 'true' : 'false',
          _addon_for: this.config.handle || 'personalisation',
          _from_sku: mainSku,
          [field.label]: sel.label,
        },
      });
    }

    return addons;
  }

  /**
   * Add-on lines are already in the cart when the main form submits, and they
   * are free — so they must not survive a main add that fails, or that never
   * starts (product-form bails out silently on a disabled button or a quantity
   * rule). The cart's own bundle reconcile only sees carts it has rendered;
   * this covers the product page.
   *
   * @param {HTMLFormElement} form
   * @param {string} bundleId
   */
  #guardAddons(form, bundleId) {
    const listener = new AbortController();
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;

    const verify = () => {
      clearTimeout(timer);
      listener.abort();
      this.#removeStrandedAddons(bundleId).catch((err) =>
        console.error('[personaliser] add-on rollback failed:', err)
      );
    };

    timer = setTimeout(verify, ADDON_GUARD_TIMEOUT);

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (event) => {
        const update = /** @type {any} */ (event);
        if (update.action !== 'add') return;
        if (!(event.target instanceof Node) || !event.target.contains(form)) return;

        // The main add is under way: its outcome decides, not the timer.
        clearTimeout(timer);
        listener.abort();

        Promise.resolve(update.promise)
          .then((result) => {
            if (result?.detail?.didError) verify();
          })
          .catch(verify);
      },
      { signal: listener.signal }
    );
  }

  /**
   * Removes this bundle's add-on lines if — and only if — the cart holds no
   * main line for it. Always decided from the real cart, never from the DOM.
   *
   * @param {string} bundleId
   */
  async #removeStrandedAddons(bundleId) {
    const cart = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    const lines = (cart.items || []).filter((item) => item.properties?._bundle_id === bundleId);
    if (lines.some((item) => !item.properties?._addon_for)) return; // main line made it

    const stranded = lines.filter((item) => item.properties?._addon_for);
    if (!stranded.length) return;

    /** @type {Set<string>} */
    const sectionIds = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'product',
        lines: stranded.map((item) => ({ id: item.key, quantity: 0 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: Object.fromEntries(stranded.map((item) => [item.key, 0])),
          sections: [...sectionIds].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const updated = await response.json();
      if (!response.ok || updated.status) throw new Error(updated.message || 'Cart update failed');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
        detail: {
          items: updated.items,
          itemCount: updated.item_count,
          sections: updated.sections,
          source: 'personaliser-component',
          didError: false,
        },
      });
    } catch (err) {
      deferred.reject(err);
      throw err;
    }
  }

  async handleSubmit() {
    this.#handleIncompleteUploads();
    this.#refresh();

    const error = this.#validate();
    if (error) {
      this.#setError(error);
      return;
    }
    this.#setError('');

    // Attach the personalisation properties to the main form for THIS submit
    this.#writeCartProperties();

    const { addButton } = this.refs;
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = this.t.adding || 'Adding…';
    }

    // Set once add-on lines are in the cart, so a failure after that point can
    // take them back out (they are free — see #guardAddons).
    let bundleId = '';

    try {
      // Resolve the form before anything goes into the cart.
      const form = this.#mainForm;
      if (!form) throw new Error('Main product form not found');

      const addons = this.#variantAddons();

      if (addons.length > 0) {
        bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        addons.forEach((addon) => {
          addon.properties._bundle_id = bundleId;
        });
        this.refs.propertiesBucket?.appendChild(this.#propertyInput('_bundle_id', bundleId));

        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: addons }),
        });

        if (!response.ok) {
          const failed = addons
            .map((addon) =>
              Object.values(this.selections).find(
                (sel) => sel && String(sel.variant_id) === String(addon.id)
              )
            )
            .find(Boolean);

          const message =
            this.t.variantOutOfStock ||
            'Sorry, this option is now out of stock. Please select a different option.';
          this.#setError(failed?.label ? message.replace('this option', `"${failed.label}"`) : message);
          this.refs.propertiesBucket?.replaceChildren();
          this.#resetAddButton();
          return;
        }
      }

      if (bundleId) this.#guardAddons(form, bundleId);

      this.closeDialog();
      // requestSubmit dispatches the submit event synchronously and the
      // product form captures FormData inside it, so clearing the bucket
      // straight after leaves the form clean for standard adds.
      form.requestSubmit();
      bundleId = ''; // the main add is under way — #guardAddons owns the add-ons from here
      this.refs.propertiesBucket?.replaceChildren();
      this.#resetAddButton();
    } catch (err) {
      console.error('[personaliser] add to cart error:', err);
      this.#setError(this.t.addToCartError || 'Failed to add to cart. Please try again.');
      this.refs.propertiesBucket?.replaceChildren();
      this.#resetAddButton();
      // No-op unless the add-ons went in and the main item did not.
      if (bundleId) this.#removeStrandedAddons(bundleId).catch(() => {});
    }
  }
}

if (!customElements.get('personaliser-component')) {
  customElements.define('personaliser-component', PersonaliserComponent);
}
